/**
 * Owner reveal of a client's vault recovery words (D81) — Owner only.
 *
 *  1. Fresh 2FA code + typed REVEAL → the server hands over LOCKED material (the encrypted key file
 *     and the client's sealed backup) and audits it.
 *  2. The Owner types the recovery passphrase. The key file and the backup are opened HERE, in the
 *     browser (@custos/vault, loaded only now). The passphrase, key and words are never sent anywhere.
 *  3. The words are shown once for 60 seconds (no copy button), kept only in this component's state
 *     and wiped when hidden or closed. "I've told the client" completes the request.
 *
 * Also: the key-file card (upload the still-encrypted vault-recovery-….key.enc, or remove it).
 *
 * Quick reveal (D87, owner's choice): set up once by opening the key file HERE with the recovery
 * passphrase and re-locking the recovery key with the Owner's admin login password. After that a
 * reveal is one screen: login password + 2FA. The server checks both and hands over the locked key;
 * this browser opens it. The key file itself doesn't need to be uploaded for this.
 */
import { ApiError, type AdminVaultRecoveryRequest, type OwnerKeyFileStatus } from "@custos/api-client";
import type { VaultSecret } from "@custos/vault";
import {
  Badge,
  Button,
  Card,
  CardTitle,
  CodeInput,
  Field,
  Input,
  Modal,
  Notice,
  formatDateTime,
} from "@custos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, KeyRound, ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { useAdminMe } from "../lib/queries";

const SHOW_SECONDS = 60;

type Revealed = { kind: "mnemonic"; words: string[] } | { kind: "privateKeys"; keys: [string, string][] };

const wrongPassword = (err: unknown, what: string) =>
  err instanceof Error && err.name === "WrongPasswordError"
    ? `Wrong ${what}.`
    : err instanceof Error
      ? err.message
      : "Couldn't open the backup.";

export function QuickRevealCard() {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ["owner-key-file"], queryFn: () => api.ownerKeyFile() });
  const [settingUp, setSettingUp] = useState(false);
  const [removing, setRemoving] = useState(false);
  const s = status.data;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["owner-key-file"] });
  if (!s?.configured) return null;
  const on = s.quickReveal.setUp;

  return (
    <Card className="mb-4 flex flex-col gap-3" data-testid="quick-reveal-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-5" aria-hidden /> Quick reveal (login password + 2FA)
        </CardTitle>
        <Badge tone={on ? "success" : "neutral"}>{on ? "on" : "off"}</Badge>
      </div>
      <p className="text-sm text-muted">
        Read a client&apos;s words with just your admin login password and a 2FA code. Set up once with your
        recovery key file and its passphrase (opened only in this browser).{" "}
        <b className="text-fg">
          Anyone with your admin password and your 2FA phone can then read every client&apos;s words
        </b>{" "}
        — keep both safe. Changing your admin password turns this off.
        {s.quickReveal.setUpAt && ` Set up ${formatDateTime(s.quickReveal.setUpAt)}.`}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={() => setSettingUp(true)}>
          {on ? "Set up again" : "Set up quick reveal"}
        </Button>
        {on && (
          <Button size="sm" variant="danger" onClick={() => setRemoving(true)}>
            Turn off
          </Button>
        )}
      </div>
      <QuickRevealSetup open={settingUp} onClose={() => setSettingUp(false)} onDone={refresh} />
      <StepUpModal
        open={removing}
        onOpenChange={setRemoving}
        title="Turn off quick reveal?"
        description="The password-locked key is deleted from the server. Your key file and words are not affected."
        confirmLabel="Turn off"
        danger
        onConfirm={(code) => api.removeQuickRevealKey(code)}
        onDone={refresh}
      />
    </Card>
  );
}

function QuickRevealSetup({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const me = useAdminMe();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; json: unknown } | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setPassphrase("");
    setPassword("");
    setCode("");
    setProblem(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  const pick = async (f: File | undefined) => {
    setProblem(null);
    if (!f) return;
    if (f.size > 20_000) return setProblem("That file is too big to be a recovery key file.");
    try {
      setFile({ name: f.name, json: JSON.parse(await f.text()) as unknown });
    } catch {
      setProblem("That file isn't a recovery key file (vault-recovery-….key.enc).");
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file || !passphrase || !password || code.length !== 6 || !me.data) return;
    setBusy(true);
    setProblem(null);
    try {
      // Loaded only now: the decryption code isn't part of the normal admin panel.
      const vault = await import("@custos/vault");
      const keyFile = vault.parseRecoveryKeyFile(file.json);
      let secretKey: Uint8Array;
      try {
        secretKey = await vault.unlockRecoveryKeyFile(keyFile, passphrase, keyFile.network);
      } catch (err) {
        throw new Error(wrongPassword(err, "recovery passphrase"), { cause: err });
      }
      try {
        const quick = await vault.wrapForQuickReveal(secretKey, password, keyFile.network, me.data.id);
        await api.storeQuickRevealKey(quick, password, code);
      } finally {
        secretKey.fill(0);
      }
      onDone();
      close();
    } catch (err) {
      // Browser-side problems (wrong passphrase, wrong file) are plain Errors with a useful message.
      setProblem(err instanceof Error && !(err instanceof ApiError) ? err.message : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && close()}
      title="Set up quick reveal"
      description="Your key file and passphrase are used only in this browser — they are not uploaded."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate data-testid="quick-reveal-setup">
        {problem && <Notice tone="danger">{problem}</Notice>}
        <Field label="Recovery key file" hint="C:\custos-secrets\vault-recovery-testnet.key.enc">
          {() => (
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={() => input.current?.click()}>
                Choose file
              </Button>
              <span className="truncate text-sm text-muted">{file?.name ?? "none chosen"}</span>
            </div>
          )}
        </Field>
        <input
          ref={input}
          type="file"
          accept=".enc,application/json"
          className="sr-only"
          tabIndex={-1}
          aria-label="Recovery key file for quick reveal"
          onChange={(e) => {
            void pick(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Field label="Recovery passphrase" hint="The passphrase of that key file.">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="off"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
            />
          )}
        </Field>
        <Field label="Your admin login password">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Field label="Your 2FA code">{(p) => <CodeInput {...p} value={code} onChange={setCode} />}</Field>
        <Button
          type="submit"
          block
          loading={busy}
          disabled={!file || !passphrase || !password || code.length !== 6}
        >
          Set up
        </Button>
      </form>
    </Modal>
  );
}

export function KeyFileCard() {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ["owner-key-file"], queryFn: () => api.ownerKeyFile() });
  const [picked, setPicked] = useState<{ name: string; json: unknown } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const s: OwnerKeyFileStatus | undefined = status.data;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["owner-key-file"] });

  const pick = async (file: File | undefined) => {
    setProblem(null);
    if (!file) return;
    if (file.size > 20_000) return setProblem("That file is too big to be a recovery key file.");
    try {
      setPicked({ name: file.name, json: JSON.parse(await file.text()) as unknown });
    } catch {
      setProblem("That file isn't a recovery key file (vault-recovery-….key.enc).");
    }
  };

  return (
    <Card className="mb-4 flex flex-col gap-3" data-testid="key-file-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-5" aria-hidden /> Recovery key file (for reading words)
        </CardTitle>
        {s && (
          <Badge tone={s.uploaded ? "success" : "neutral"}>{s.uploaded ? "uploaded" : "not uploaded"}</Badge>
        )}
      </div>
      {s && !s.configured ? (
        <p className="text-sm text-muted">
          Vault recovery isn&apos;t set up on this server (no VAULT_RECOVERY_PUBLIC_KEY).
        </p>
      ) : (
        <>
          <p className="text-sm text-muted">
            To read a client&apos;s words here, upload your <b>encrypted</b> key file from{" "}
            <code className="font-mono text-xs">C:\custos-secrets\vault-recovery-…key.enc</code>. It stays
            locked with your passphrase — the server can&apos;t open it. A long passphrase is what protects
            it.
            {s?.uploadedAt && ` Uploaded ${formatDateTime(s.uploadedAt)}.`}
          </p>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => input.current?.click()}>
              {s?.uploaded ? "Replace key file" : "Upload key file"}
            </Button>
            {s?.uploaded && (
              <Button size="sm" variant="danger" onClick={() => setRemoving(true)}>
                Remove
              </Button>
            )}
          </div>
          <input
            ref={input}
            type="file"
            accept=".enc,application/json"
            className="sr-only"
            tabIndex={-1}
            aria-label="Recovery key file"
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </>
      )}
      <StepUpModal
        open={picked !== null}
        onOpenChange={(o) => !o && setPicked(null)}
        title="Upload the recovery key file?"
        description={picked ? `${picked.name} — it stays encrypted with your passphrase.` : ""}
        confirmLabel="Upload key file"
        onConfirm={(code) => api.uploadOwnerKeyFile(picked!.json, code)}
        onDone={refresh}
      />
      <StepUpModal
        open={removing}
        onOpenChange={setRemoving}
        title="Remove the key file from the server?"
        description="You can't read words in the panel until you upload it again. Your PC copy is not affected."
        confirmLabel="Remove"
        danger
        onConfirm={(code) => api.removeOwnerKeyFile(code)}
        onDone={refresh}
      />
    </Card>
  );
}

export function RevealModal({
  request,
  onClose,
}: {
  request: AdminVaultRecoveryRequest | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const me = useAdminMe();
  const status = useQuery({ queryKey: ["owner-key-file"], queryFn: () => api.ownerKeyFile() });
  const quickOn = status.data?.quickReveal.setUp ?? false;
  const [stage, setStage] = useState<"quick" | "confirm" | "passphrase" | "words" | "hidden">("confirm");
  const [typed, setTyped] = useState("");
  const [code, setCode] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [password, setPassword] = useState("");
  const material = useRef<Awaited<ReturnType<typeof api.revealMaterial>> | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [left, setLeft] = useState(SHOW_SECONDS);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Fresh start for every request; everything is wiped on close.
  useEffect(() => {
    setStage(quickOn ? "quick" : "confirm");
    setTyped("");
    setCode("");
    setPassphrase("");
    setPassword("");
    setRevealed(null);
    setProblem(null);
    material.current = null;
  }, [request, quickOn]);

  // Shown for 60 s, then wiped.
  useEffect(() => {
    if (stage !== "words") return;
    setLeft(SHOW_SECONDS);
    const t = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          setRevealed(null);
          setStage("hidden");
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [stage]);

  const close = () => {
    setRevealed(null);
    setPassphrase("");
    setPassword("");
    material.current = null;
    onClose();
  };

  const show = (secret: VaultSecret) =>
    setRevealed(
      secret.kind === "mnemonic"
        ? { kind: "mnemonic", words: secret.mnemonic.split(" ") }
        : {
            kind: "privateKeys",
            keys: Object.entries(secret.keys).filter((kv): kv is [string, string] => !!kv[1]),
          },
    );

  /** Quick reveal (D87): login password + 2FA → the server checks both → the key is opened HERE. */
  const quickReveal = async (e: FormEvent) => {
    e.preventDefault();
    if (!request || !password || code.length !== 6 || !me.data) return;
    setBusy(true);
    setProblem(null);
    try {
      const m = await api.quickRevealMaterial(request.id, password, code);
      const vault = await import("@custos/vault");
      const secretKey = await vault.unlockQuickRevealKey(
        vault.parseQuickRevealKey(m.quickKey),
        password,
        m.network,
        me.data.id,
      );
      try {
        show(vault.openBackupForOwner(secretKey, m.backup, m.network, m.userId));
      } finally {
        secretKey.fill(0);
      }
      setPassword("");
      setCode("");
      setStage("words");
    } catch (err) {
      setProblem(
        err instanceof Error && err.name === "WrongPasswordError"
          ? "Quick reveal no longer matches your password — set it up again."
          : err instanceof Error && !(err instanceof ApiError)
            ? err.message
            : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const fetchMaterial = async (e: FormEvent) => {
    e.preventDefault();
    if (!request || typed !== "REVEAL" || code.length !== 6) return;
    setBusy(true);
    setProblem(null);
    try {
      material.current = await api.revealMaterial(request.id, typed, code);
      setStage("passphrase");
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (e: FormEvent) => {
    e.preventDefault();
    const m = material.current;
    if (!m || !passphrase) return;
    setBusy(true);
    setProblem(null);
    try {
      // Loaded only now: the decryption code isn't part of the normal admin panel.
      const vault = await import("@custos/vault");
      const secretKey = await vault.unlockRecoveryKeyFile(
        vault.parseRecoveryKeyFile(m.keyFile),
        passphrase,
        m.network,
      );
      try {
        show(vault.openBackupForOwner(secretKey, m.backup, m.network, m.userId));
      } finally {
        secretKey.fill(0);
      }
      setPassphrase("");
      setStage("words");
    } catch (err) {
      setProblem(wrongPassword(err, "passphrase"));
    } finally {
      setBusy(false);
    }
  };

  const complete = useMutation({
    mutationFn: () => api.markRevealed(request!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "vault-recovery"] });
      close();
    },
  });

  return (
    <Modal
      open={request !== null}
      onOpenChange={(o) => !o && close()}
      title="Read the recovery words"
      {...(request ? { description: `For ${request.email}. Only after checking their identity.` } : {})}
    >
      <div className="flex flex-col gap-4" data-testid="reveal-modal">
        {problem && <Notice tone="danger">{problem}</Notice>}

        {stage === "quick" && (
          <form
            onSubmit={quickReveal}
            className="flex flex-col gap-4"
            noValidate
            data-testid="quick-reveal-form"
          >
            <Notice tone="warning">
              <span className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  Do this alone, on your own computer, with no screen sharing or recording. Tell the words to
                  the client only over a line you trust — never by chat, email or text message.
                </span>
              </span>
            </Notice>
            <Field label="Your admin login password">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label="Your 2FA code">{(p) => <CodeInput {...p} value={code} onChange={setCode} />}</Field>
            <Button type="submit" block loading={busy} disabled={!password || code.length !== 6}>
              Show the words
            </Button>
          </form>
        )}

        {stage === "confirm" && (
          <form onSubmit={fetchMaterial} className="flex flex-col gap-4" noValidate>
            <Notice tone="warning">
              <span className="flex items-start gap-2">
                <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  Do this alone, on your own computer, with no screen sharing or recording. Tell the words to
                  the client only over a line you trust — never by chat, email or text message.
                </span>
              </span>
            </Notice>
            <Field label="Type REVEAL to continue">
              {(p) => (
                <Input {...p} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
              )}
            </Field>
            <Field label="Your 2FA code">{(p) => <CodeInput {...p} value={code} onChange={setCode} />}</Field>
            <Button type="submit" block loading={busy} disabled={typed !== "REVEAL" || code.length !== 6}>
              Continue
            </Button>
          </form>
        )}

        {stage === "passphrase" && (
          <form onSubmit={unlock} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-muted">
              Type the passphrase of your recovery key file. It is used only in this browser and is never sent
              to the server. Unlocking takes a few seconds.
            </p>
            <Field label="Recovery passphrase">
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="off"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" block loading={busy} disabled={!passphrase}>
              Unlock and show
            </Button>
          </form>
        )}

        {stage === "words" && revealed && (
          <>
            <p className="text-sm text-muted" aria-live="polite">
              Hidden again in <b className="text-fg tabular-nums">{left}</b> s.
            </p>
            {revealed.kind === "mnemonic" ? (
              <ol className="grid grid-cols-2 gap-2 select-none sm:grid-cols-3" data-testid="revealed-words">
                {revealed.words.map((w, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 rounded-field border border-line bg-soft px-3 py-2"
                  >
                    <span className="w-5 text-right text-xs text-muted tabular-nums">{i + 1}</span>
                    <span className="font-mono text-sm text-fg">{w}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <ul className="flex flex-col gap-2 select-none" data-testid="revealed-keys">
                {revealed.keys.map(([family, key]) => (
                  <li key={family} className="rounded-field border border-line bg-soft p-3">
                    <p className="text-xs text-muted">{family} private key</p>
                    <p className="font-mono text-sm break-all text-fg">{key}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="secondary"
                block
                onClick={() => {
                  setRevealed(null);
                  setStage("hidden");
                }}
              >
                <EyeOff className="size-4" aria-hidden /> Hide now
              </Button>
              <Button block loading={complete.isPending} onClick={() => complete.mutate()}>
                I&apos;ve told the client — complete
              </Button>
            </div>
          </>
        )}

        {stage === "hidden" && (
          <>
            <p className="text-sm text-muted">The words are hidden and wiped from this page.</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="secondary" block onClick={close}>
                Close (not finished)
              </Button>
              <Button block loading={complete.isPending} onClick={() => complete.mutate()}>
                I&apos;ve told the client — complete
              </Button>
            </div>
          </>
        )}
        {complete.error && <Notice tone="danger">{errorMessage(complete.error)}</Notice>}
      </div>
    </Modal>
  );
}
